'use client';

import { useState, useEffect } from 'react';
import { Button } from '@kit/ui/button';
import { MessageCircle, X } from 'lucide-react';
import { cn } from '@kit/ui/utils';

interface ChatOverlayButtonProps {
  title: string;
  url: string;
}

export function ChatOverlayButton({ title, url }: ChatOverlayButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: '400px',
    height: 'calc(100vh - 6rem)'
  });

  useEffect(() => {
    const updateDimensions = () => {
      const isMobile = window.innerWidth < 640;
      const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;
      
      // Account for: button (3.5rem) + bottom spacing (1.5rem) + margin (1rem) + container margins (mt-4 + mb-4 = 2rem)
      const bottomSpacing = '8rem';
      
      if (isMobile) {
        setDimensions({
          width: 'calc(100vw - 3rem)',
          height: `calc(100vh - ${bottomSpacing})`
        });
      } else if (isTablet) {
        setDimensions({
          width: 'min(500px, 75vw)',
          height: `calc(100vh - ${bottomSpacing})`
        });
      } else {
        setDimensions({
          width: '400px',
          height: `calc(100vh - ${bottomSpacing})`
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <div 
        className={cn(
          "mt-4 mb-4 rounded-md shadow-lg overflow-hidden border border-border bg-background",
          "transition-all duration-300 ease-in-out transform",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        style={isOpen ? dimensions : { width: '0px', height: '0px' }}
      >
        <iframe 
          src={url}
          title={title}
          className="w-full h-full"
          allow="fullscreen; clipboard-read; clipboard-write; cookies" 
          loading="lazy"
          referrerPolicy="origin"
        />
      </div>
      <Button 
        size="sm" 
        className={cn(
          "h-14 w-14 rounded-full shadow-lg cursor-pointer transition-transform duration-200",
          isOpen ? "rotate-0" : "rotate-0"
        )}
        aria-label={isOpen ? "Close Chat" : "Open Chat"}
        onClick={toggleChat}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
    </div>
  );
}